import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  ArrowRight,
  Landmark,
  Crown,
  Users,
  Briefcase,
  Flower2,
  HeartHandshake,
} from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import NextBestAction from "@/components/NextBestAction";
import usePageSEO from "@/hooks/usePageSEO";

/**
 * Phase 13.3 — High Net Worth landing. Compliance-first framing:
 *  - "A plan that works like family office — priced like it should" → avoided
 *    the specific pricing claim since we can't substantiate it. Replaced with
 *    "A plan that works like family office — without the family-office minimum."
 *    which is factual (there's no literal AUM minimum) and non-promissory.
 */
const PILLARS = [
  {
    Icon: Crown,
    title: "Premium-Financed IUL",
    body: "Tax-advantaged accumulation with a death-benefit chassis. Appropriate when there's sustained high income, a good underwriting profile, and a long time horizon.",
  },
  {
    Icon: Landmark,
    title: "Advanced Estate Planning",
    body: "Coordinated with your estate attorney — ILITs, GRATs, dynasty-trust funding strategies, and liquidity planning for estate tax.",
  },
  {
    Icon: Users,
    title: "Multi-Generational Wealth Transfer",
    body: "Structured so children inherit the asset, not the tax bill — and grandchildren inherit the asset, not a court case.",
  },
  {
    Icon: Briefcase,
    title: "Business Owner Structure Integration",
    body: "Business wealth and personal wealth are usually the same wealth. We coordinate buy-sell, key-person, deferred comp, and personal IUL in one coherent structure.",
  },
  {
    Icon: Flower2,
    title: "Private Placement / Alternative Assets — Education",
    body: "We provide educational context on PPLI, hedge-fund-of-fund wrappers, and alternative exposure. These are not products we sell directly.",
  },
  {
    Icon: HeartHandshake,
    title: "Charitable Giving Strategy",
    body: "CRTs, CLATs, donor-advised fund coordination. We support the strategy — your tax attorney and CPA structure the actual vehicles.",
  },
];

const CASE_STUDIES = [
  {
    title: "$10M family business owner — succession + estate structure",
    story:
      "Owner, 58, with three adult children (one in the business, two outside). Primary concerns: fair treatment at death, continuity of business, estate liquidity for the IRS bill.",
    structure: [
      "Business valuation + funded buy-sell with the in-business child",
      "Life insurance in an ILIT sized to equalize the estate for out-of-business children",
      "Deferred comp tied to tenure and post-sale non-compete",
      "Coordination with the estate attorney on dynasty-trust funding strategy",
    ],
  },
  {
    title: "$5M retired executive — tax-efficient income + legacy",
    story:
      "Couple, 66 and 64, net worth concentrated in deferred-comp balances and a concentrated stock position from the retiring spouse's former employer.",
    structure: [
      "Partial annuitization for lifetime income on a portion of the portfolio",
      "IUL funded over 7-pay period for tax-free supplementation",
      "LTC rider on the IUL contract to address long-term-care exposure",
      "Charitable remainder trust for the concentrated stock, coordinated with their CPA",
    ],
  },
];

export default function HighNetWorth() {
  usePageSEO({
    title: "High Net Worth Planning | Lifespan Wealth Partners",
    description:
      "Family-office-style planning for $2M+ net worth families — premium-financed IUL, estate structures, multi-generational wealth transfer, and charitable giving coordination. Educational framing, NAIC-compliant.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FinancialProduct",
      name: "High Net Worth Planning",
      provider: { "@id": "https://lifespanwealthpartners.com/#org" },
      category: "Wealth Planning & Estate Strategy",
    },
  });

  return (
    <div data-testid="hnw-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main>
        {/* Hero */}
        <section className="pt-28 sm:pt-32 lg:pt-36 pb-16 hero-grain">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="lw-eyebrow">For High Net Worth Families</span>
              <h1
                className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-lw-navy"
                data-testid="hnw-hero-headline"
              >
                Wealth that <span className="italic">outlives</span> you.
                <br className="hidden sm:block" />
                A plan that works like family office —{" "}
                <span className="italic text-lw-gold">
                  without the family-office minimum.
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-lw-muted">
                For families typically above $2M net worth — business owners,
                professionals, inheritors — who want the coordination of a
                private-wealth office without re-building their life around a
                single provider.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/apply?audience=hnw" className="btn-gold">
                  Schedule a consultation <ArrowRight size={18} />
                </Link>
                <Link to="/team#dharam-chaudhari" className="btn-ghost-navy">
                  Meet Dharam Chaudhari
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <WealthTransferMini />
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="lw-eyebrow">Six Pillars</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
                How we structure <span className="italic">generational wealth.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {PILLARS.map(({ Icon, title, body }, i) => (
                <article
                  key={title}
                  data-testid={`hnw-pillar-${i}`}
                  className="lw-card p-6"
                >
                  <div className="w-11 h-11 rounded-xl bg-lw-navy text-lw-gold flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-5 font-serif-display text-lg text-lw-navy leading-tight">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-lw-muted">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Scenario modeler */}
        <EstateScenarioModeler />

        {/* Dharam callout */}
        <section className="py-16 bg-lw-ivory-warm">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-2 flex justify-center">
              <div className="w-48 h-48 rounded-full overflow-hidden border-2 border-lw-gold/40 bg-white">
                <img
                  src="/assets/brand/advisor-dharam-chaudhari.jpg"
                  alt="Dharam Chaudhari"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <span className="lw-eyebrow">Your HNW Lead</span>
              <h2 className="mt-3 font-serif-display text-3xl text-lw-navy leading-tight">Dharam Chaudhari</h2>
              <p className="mt-1 font-sans-ui text-[0.72rem] tracking-[0.2em] uppercase text-lw-gold">
                Co-Founder / Head of Strategy
              </p>
              <p className="mt-4 text-sm leading-relaxed text-lw-muted">
                Specializes in wealth planning strategies for high-net-worth
                individuals, generational wealth structuring, and advanced
                financial coordination.
              </p>
              <Link
                to="/apply?audience=hnw&advisor=dharam-chaudhari"
                data-testid="hnw-cta-dharam"
                className="btn-gold mt-6 text-sm"
              >
                Schedule with Dharam <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* Case studies */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="lw-eyebrow">Illustrative Scenarios</span>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
                How this looks in <span className="italic">real life.</span>
              </h2>
              <p className="mt-3 text-sm text-lw-muted">
                Anonymized composite cases — not specific client advice.
              </p>
            </div>
            <div className="space-y-6">
              {CASE_STUDIES.map((cs, i) => (
                <article
                  key={i}
                  data-testid={`hnw-case-${i}`}
                  className="rounded-2xl border border-lw-navy/10 bg-lw-ivory-warm/60 p-7 sm:p-9"
                >
                  <h3 className="font-serif-display text-xl sm:text-2xl text-lw-navy leading-tight">{cs.title}</h3>
                  <p className="mt-3 text-sm text-lw-muted leading-relaxed">{cs.story}</p>
                  <div className="mt-5 pt-5 border-t border-lw-navy/10">
                    <div className="font-sans-ui text-[0.68rem] tracking-[0.2em] uppercase text-lw-gold mb-3">
                      How we structured it
                    </div>
                    <ul className="space-y-2">
                      {cs.structure.map((s, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-sm leading-relaxed text-lw-ink">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-lw-gold flex-shrink-0" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Carrier strip (HNW-specific) */}
        <section className="py-16 bg-lw-ivory-warm">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <span className="lw-eyebrow">Carriers Relevant To HNW Planning</span>
            <h2 className="mt-3 font-serif-display text-2xl text-lw-navy">
              Access without captive incentives.
            </h2>
            <ul className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
              {["Lincoln Financial", "Nationwide", "Allianz", "F&G", "North American"].map((c) => (
                <li key={c} className="rounded-xl border border-lw-navy/10 bg-white p-4 text-center font-serif-display text-sm sm:text-base text-lw-navy">
                  {c}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-lw-muted">
              Carrier selection depends on underwriting outcome, state availability,
              and the specific product structure. Not an endorsement.
            </p>
          </div>
        </section>

        {/* Compliance bar */}
        <section className="py-10 bg-white">
          <div className="max-w-4xl mx-auto px-5 text-xs text-lw-muted leading-relaxed">
            Illustrative scenarios only. Not tax or legal advice. Insurance is one
            component of a comprehensive HNW plan — coordination with your tax
            attorney, estate attorney, and CPA is essential. Insurance and annuity
            guarantees are backed by the financial strength and claims-paying
            ability of the issuing carrier. Lifespan Wealth Partners does not
            provide registered investment advisory services or sell private
            placement products.
          </div>
        </section>

        <NextBestAction
          pageId="high-net-worth"
          step={4}
          heading="Schedule a confidential consultation with Dharam Chaudhari"
          rationale="HNW planning is bespoke. A 45-minute call with our Co-Founder / Head of Strategy maps estate-level solutions to your actual balance sheet — no illustrations, just a conversation."
          primary={{ label: "Request a consultation", to: "/apply?source=hnw", testid: "nba-hnw-primary" }}
          secondary={{ label: "Explore premium-financed IUL", to: "/products/premium-financed-iul", testid: "nba-hnw-secondary" }}
        />
      </main>
      <Footer />
    </div>
  );
}

/* ---- Visuals ---- */
function WealthTransferMini() {
  return (
    <div className="relative rounded-[1.75rem] overflow-hidden border border-lw-gold/40 shadow-[0_30px_80px_-30px_rgba(10,37,64,0.35)] bg-lw-navy p-8">
      <div className="font-sans-ui text-[0.65rem] tracking-[0.22em] uppercase text-lw-gold mb-6">
        Four-generation wealth transfer
      </div>
      <svg viewBox="0 0 400 220" className="w-full h-auto">
        <defs>
          <linearGradient id="hnw-arrow" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="1" />
          </linearGradient>
        </defs>
        {[
          { x: 40, label: "G1", sublabel: "Grandparent", r: 28 },
          { x: 140, label: "G2", sublabel: "Parent", r: 30 },
          { x: 250, label: "G3", sublabel: "You / Children", r: 32 },
          { x: 360, label: "G4", sublabel: "Grandchildren", r: 30 },
        ].map((g, i, arr) => (
          <g key={g.label}>
            <circle cx={g.x} cy={100} r={g.r} fill="#FAF7F2" stroke="#D4AF37" strokeWidth="2" />
            <text x={g.x} y={105} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="600" fill="#0A2540">
              {g.label}
            </text>
            <text x={g.x} y={160} textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="10" fill="#D4AF37">
              {g.sublabel}
            </text>
            {i < arr.length - 1 && (
              <>
                <line x1={g.x + g.r + 2} y1={100} x2={arr[i + 1].x - arr[i + 1].r - 6} y2={100} stroke="url(#hnw-arrow)" strokeWidth="3" strokeLinecap="round" />
                <polygon
                  points={`${arr[i + 1].x - arr[i + 1].r - 6},${100} ${arr[i + 1].x - arr[i + 1].r - 12},${96} ${arr[i + 1].x - arr[i + 1].r - 12},${104}`}
                  fill="#D4AF37"
                />
              </>
            )}
          </g>
        ))}
      </svg>
      <p className="mt-4 text-[0.7rem] text-[#FAF7F2]/60 leading-snug">
        Structured so each generation inherits the asset — not the tax bill.
      </p>
    </div>
  );
}

function EstateScenarioModeler() {
  const [netWorth, setNetWorth] = useState(5_000_000);
  const [heirs, setHeirs] = useState(3);

  const data = useMemo(() => {
    // Simplified estimated federal estate-tax exposure using the 2026
    // lifetime exemption projection (~$7M/person, $14M/couple).
    const EXEMPTION_PER_SPOUSE = 7_000_000;
    const JOINT_EXEMPTION = EXEMPTION_PER_SPOUSE * 2;
    const taxable = Math.max(0, netWorth - JOINT_EXEMPTION);
    const taxEstimate = Math.round(taxable * 0.4);
    const withoutHeir = Math.round((netWorth - taxEstimate) / heirs);
    const withHeir = Math.round(netWorth / heirs);
    return [
      { name: "Per heir — without planning", value: withoutHeir },
      { name: "Per heir — with structured plan", value: withHeir },
    ];
  }, [netWorth, heirs]);

  return (
    <section className="py-20 bg-lw-ivory-warm">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="lw-eyebrow">Estate Scenario Modeler</span>
          <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">
            A quick look at <span className="italic">generational math.</span>
          </h2>
          <p className="mt-3 text-sm text-lw-muted">
            Illustrative only — assumes a simplified $14M joint federal lifetime
            exemption and a 40% effective tax on the taxable estate. Your actual
            situation requires full estate-tax modeling with your attorney and CPA.
          </p>
        </div>
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-5 space-y-5 bg-white rounded-2xl border border-lw-navy/10 p-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-sans-ui text-xs font-medium tracking-wide text-lw-navy uppercase">
                  Taxable estate (net worth)
                </label>
                <span className="font-serif-display text-base text-lw-navy tabular-nums">
                  ${(netWorth / 1_000_000).toFixed(1)}M
                </span>
              </div>
              <input
                type="range"
                min={1_000_000}
                max={50_000_000}
                step={500_000}
                value={netWorth}
                data-testid="hnw-modeler-networth"
                onChange={(e) => setNetWorth(Number(e.target.value))}
                className="w-full accent-[#D4AF37] cursor-pointer"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-sans-ui text-xs font-medium tracking-wide text-lw-navy uppercase">
                  Heirs
                </label>
                <span className="font-serif-display text-base text-lw-navy tabular-nums">{heirs}</span>
              </div>
              <input
                type="range"
                min={1}
                max={8}
                step={1}
                value={heirs}
                data-testid="hnw-modeler-heirs"
                onChange={(e) => setHeirs(Number(e.target.value))}
                className="w-full accent-[#D4AF37] cursor-pointer"
              />
            </div>
          </div>
          <div className="lg:col-span-7 rounded-2xl border border-lw-navy/10 bg-white p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#0A2540" strokeOpacity="0.08" />
                <XAxis type="number" tick={{ fill: "#5A6472", fontSize: 11 }} tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`} />
                <YAxis dataKey="name" type="category" width={200} tick={{ fill: "#0A2540", fontSize: 12 }} />
                <Tooltip cursor={{ fill: "rgba(212,175,55,0.08)" }} formatter={(v) => `$${v.toLocaleString()}`} />
                <Bar dataKey="value" fill="#D4AF37" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="mt-3 text-xs text-lw-muted leading-relaxed">
              Illustrative only. Actual estate-tax exposure depends on exemption
              portability, state estate tax, grantor-trust strategy, business
              valuation discounts, and many other factors. Consult your attorney and CPA.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
